import { createFileRoute, Link } from "../lib/router-compat";
import { PageShell, PanImage } from "../components/PageShell";

export const Route = createFileRoute("/atlantic-clarion")({
  head: () => ({
    meta: [
      { title: "Atlantic Clarion Steel Band — Professionals Since 1974" },
      {
        name: "description",
        content:
          "Atlantic Clarion Steel Band performs concerts, parties, graduations, weddings, funerals, and events across Maine and beyond.",
      },
      { property: "og:title", content: "Atlantic Clarion Steel Band" },
      {
        property: "og:description",
        content:
          "Performances for concerts, parties, graduations, weddings, funerals, and more.",
      },
    ],
  }),
  component: ClarionPage,
});

const GALLERY = [
  "1468945234.jpg",
  "acsb-wide 3.jpg",
  "1475694459.JPG",
  "acsb_outside_closeup 2.jpg",
  "parade3.jpg",
  "unnamed-1-b99c3da.jpg",
  "unnamed-46bac39.jpg",
  "acsb_monhegan_03-3 2.jpg",
  "nigel 2.jpg",
  "band.jpg",
];

function ClarionPage() {
  return (
    <PageShell
      panBackdrop={true}
      tag="Professionals since 1974"
      title={<>Atlantic Clarion <span className="text-primary">Steel Band.</span></>}
      intro="Concerts, parties, graduations, weddings, funerals — and everything between."
    >
      {/* Book now — bold CTA */}
      <div className="relative overflow-hidden rounded-2xl border border-border/40 bg-gradient-to-br from-primary/10 via-transparent to-transparent p-10 md:p-16">
        <div className="grid md:grid-cols-12 gap-8 items-end">
          <div className="md:col-span-8">
            <div className="text-[10px] uppercase tracking-[0.5em] text-primary mb-4">Available year-round</div>
            <h2 className="font-display italic text-4xl md:text-7xl leading-[0.95]">
              Book the<br/><span className="text-primary">Clarion.</span>
            </h2>
          </div>
          <div className="md:col-span-4 md:text-right">
            <Link
              to="/contact"
              className="group inline-flex items-center gap-4 text-[11px] tracking-[0.35em] uppercase text-primary hover:text-primary/70 transition"
            >
              <span className="h-px w-12 bg-primary group-hover:w-20 transition-all" />
              Get in touch
            </Link>
          </div>
        </div>
      </div>

      {/* Editorial hero image — full bleed */}
      <figure className="mt-16 -mx-6 md:mx-0 relative">
        <PanImage
          id="blob-4a1fe6f.png"
          alt="Atlantic Clarion Steel Band"
          className="w-full h-[380px] md:h-[560px] object-cover grayscale-[15%]"
          width={1800}
        />
        <figcaption className="absolute bottom-4 right-4 md:bottom-5 md:right-5 bg-background/85 backdrop-blur px-3 py-2 text-[10px] tracking-[0.4em] uppercase text-foreground/70">
          On stage · Maine
        </figcaption>
      </figure>

      {/* Marquee strip — occasions */}
      <div className="mt-16 -mx-5 md:mx-0 border-y border-border/40 py-5 overflow-hidden">
        <div className="flex gap-10 whitespace-nowrap animate-[marquee_40s_linear_infinite] font-display italic text-2xl md:text-3xl text-foreground/50">
          {Array.from({ length: 2 }).map((_, k) => (
            <span key={k} className="flex gap-10">
              {["Concerts", "Weddings", "Parades", "Graduations", "Festivals", "Funerals", "Parties", "Galas", "Whatever you've got"].map((w) => (
                <span key={w} className="flex items-center gap-10">
                  <span>{w}</span>
                  <span className="text-primary">·</span>
                </span>
              ))}
            </span>
          ))}
        </div>
      </div>

      {/* Lede — copy block */}
      <div className="mt-20 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-4">
          <h2 className="font-display italic text-3xl md:text-4xl leading-[1.05]">
            World-class steel pan, for any occasion.
          </h2>
        </div>
        <div className="md:col-span-7 md:col-start-6 space-y-5 text-[16px] leading-relaxed text-foreground/75">
          <p className="first-letter:font-display first-letter:italic first-letter:text-6xl first-letter:float-left first-letter:mr-3 first-letter:leading-[0.9] first-letter:text-primary">
            Atlantic Clarion is a premier ensemble specializing in world-class steel pan performances. Our musicians create unforgettable experiences for clients and their guests.
          </p>
          <p>
            A small group of dedicated professionals — the music is beautiful, but it also has the power to uplift and inspire audiences of all ages. Let us enhance your event with our blend of Caribbean and world music.
          </p>
        </div>
      </div>

      {/* Gallery — varied editorial mosaic */}
      <div className="mt-28">
        <div className="flex items-end justify-between gap-6 mb-8">
          <div>
            <div className="text-[10px] uppercase tracking-[0.5em] text-primary/80">The band</div>
            <h2 className="mt-3 font-display italic text-3xl md:text-5xl">Players, past & present</h2>
          </div>
          <div className="hidden md:block text-[10px] uppercase tracking-[0.4em] text-foreground/40">
            {GALLERY.length} frames
          </div>
        </div>
        <div className="grid grid-cols-12 gap-2 md:gap-3 auto-rows-[100px] md:auto-rows-[140px]">
          {GALLERY.map((id, i) => {
            const spans = [
              "col-span-7 row-span-3",
              "col-span-5 row-span-2",
              "col-span-5 row-span-2",
              "col-span-4 row-span-2",
              "col-span-8 row-span-2",
              "col-span-6 row-span-3",
              "col-span-6 row-span-2",
              "col-span-6 row-span-2",
              "col-span-3 row-span-2",
              "col-span-3 row-span-2",
            ];
            return (
              <div key={id} className={`overflow-hidden ${spans[i] || "col-span-4 row-span-2"}`}>
                <PanImage id={id} alt="Atlantic Clarion" width={1100} className="w-full h-full object-cover grayscale-[15%] hover:grayscale-0 hover:scale-[1.02] transition-all duration-500" />
              </div>
            );
          })}
        </div>
      </div>

      <style>{`
        @keyframes marquee {
          from { transform: translateX(0); }
          to { transform: translateX(-50%); }
        }
      `}</style>
    </PageShell>
  );
}
